import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ProductImportLogModel, ProductImportWarningModel, ProductModel, RequestParamModel} from '../models';
import {PaginationModel} from '../models';
import {BaseService} from '../services';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {

  constructor(private baseService: BaseService) {
  }

  // get all user list
  getProduct(requestParamModel: RequestParamModel): any {
    return this.baseService.get('product', requestParamModel)
      .pipe(
        map<any, any>(response => {
          const productList = new PaginationModel<ProductModel>();
          productList.setHeaders(response.headers);
          productList.data = response.body.data as ProductModel[];
          return productList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getProductLog(requestParamModel: RequestParamModel): any {
    return this.baseService.get('product-import', requestParamModel)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<ProductModel>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as ProductModel[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getTempProduct(requestParamModel: RequestParamModel): any {
    return this.baseService.get('tmp-product', requestParamModel)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<ProductModel>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as ProductModel[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  //Product import detail
  getProductImportDetail(id: any): any {
    return this.baseService.get('product-import/' + id)
      .pipe(map<any, any>(response => {
          const accountList = response.body.data as ProductImportLogModel;
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  //Product import detail
  getProductAccessories(requestParam: RequestParamModel): any {
    return this.baseService.get('product-accessories', requestParam)
      .pipe(map<any, any>(response => {
          const accountList = response.body.data as any;
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getImportProductWarnning(requestParamModel: RequestParamModel): any {
    return this.baseService.get('product-import-warning', requestParamModel)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<ProductImportWarningModel>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as ProductImportWarningModel[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  //Product import detail
  getShippingImportDetail(id: any): any {
    return this.baseService.get('shipping-import/' + id)
      .pipe(map<any, any>(response => {
          const accountList = response.body.data as any;
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getCollectionImportDetail(id: any): any {
    return this.baseService.get('collection-import/' + id)
      .pipe(map<any, any>(response => {
          const accountList = response.body.data as any;
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // get single user details.
  getProductDetails(productID: number, requestParamModel: RequestParamModel): Observable<ProductModel> {
    if (productID !== 0) {
      return this.baseService.get('product/' + productID, requestParamModel)
        .pipe(map<any, ProductModel>(response => {
            return response.body.data as ProductModel;
          }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.get('product', requestParamModel)
        .pipe(map<any, ProductModel>(response => {
            return response.body.data[0] as ProductModel;
          }),
          catchError(err => throwError(() => err))
        );
    }
  }

  getProductResourceUrl(id: any) {
    return this.baseService.get('product/download-resource/' + id).pipe(map(response => {
      return response.body;
    }, (error: any) => {
      return error;
    }))
  }

  // get single user details.
  getProductImportDetails(productImportId: number): Observable<ProductImportLogModel> {
    return this.baseService.get('product-import/' + productImportId)
      .pipe(map<any, ProductImportLogModel>(response => {
          return response.body.data as ProductImportLogModel;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // save user if new then add if old then update
  saveProduct(accountModel: any): Observable<number> {
    if (accountModel.id !== undefined) {
      return this.baseService.patch('product/' + accountModel.id, accountModel)
        .pipe(map<any, number>(response => {
            accountModel = response.data as ProductModel;
            return accountModel.id;
          }),
          catchError(err => throwError(() => err))
        );
    } else {
      return this.baseService.post('product', accountModel)
        .pipe(map<any, number>(response => {
            accountModel = response.data as ProductModel;
            return accountModel.id;
          }),
          catchError(err => throwError(() => err))
        );
    }
  }

  makeProductAsFeature(accountModel: any) {
    // save user if new then add if old then update
    return this.baseService.post('product/set-feature/' + accountModel.id, accountModel)
      .pipe(map<any, number>(response => {
          return response.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  makeProductAsAllInOne(model: any) {
    // save user if new then add if old then update
    return this.baseService.post('product/set-product-as-all-in-one', model)
      .pipe(map<any, number>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
      );
  }

  applyProduct(model: any) {
    // save user if new then add if old then update
    return this.baseService.post('product-import/apply', model)
      .pipe(map<any, number>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
      );
  }

  /**
   * Assembled products: fetches from Unleashed every recipe that is waiting — the ones a product
   * sheet queued through its BOM ID column, plus earlier failures. The hourly job does the same, so
   * this only brings it forward to the moment a sheet is applied.
   */
  syncPendingBoms(): Observable<any> {
    return this.baseService.post('product/sync-pending-boms', {})
      .pipe(map<any, any>(response => response?.data),
        catchError(err => throwError(() => err))
      );
  }

  deleteProduct(_id: any) {
    return this.baseService.delete('product/' + _id)
      .pipe(map<any, number>(response => {
          return response;
        }),
        catchError(err => throwError(() => err))
      );
  }

  uploadProductsheet(requestModel: any): Observable<any> {
    return this.baseService.uploadPost('product-import', requestModel)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  uploadShippingSheet(requestModel: any): Observable<any> {
    return this.baseService.uploadPost('shipping-import', requestModel)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  uploadCollectionSheet(requestModel: any): Observable<any> {
    return this.baseService.uploadPost('collection-import', requestModel)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  updateProductOrder(requestModel: any): Observable<any> {
    return this.baseService.post('product-image/update-order', requestModel)
      .pipe(map<any, any>(response => {
          // return responseModel.id;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // get product accessories details.
  getAccessoriesFormatDetails(productID: number): any {
    return this.baseService.get('product/get-accessories-formats?product_id=' + productID)
      .pipe(map<any, any>(response => {
          const productList = new PaginationModel<ProductModel>();
          const headers = response.headers;
          productList.setHeaders(headers);
          productList.data = response.body.data as ProductModel[];
          return productList;
          return productList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getFilterAccessoriesProductDropdown(requestParamModel: RequestParamModel) {
    return this.baseService.get('product/filter-accessories-dropdown', requestParamModel)
      .pipe(map<any, any>(response => {
          const productList = response.body.data as any[];
          return productList;
          return productList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // get product details.
  getDetails(productID: number, requestParamModel?: RequestParamModel): Observable<ProductModel> {
    return this.baseService.get('product/' + productID, requestParamModel)
      .pipe(map<any, ProductModel>(response => {
          return response.body.data as ProductModel;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // get product accessories details.
  getAccessoriesTypeDetails(productID: number): any {
    return this.baseService.get('product/get-accessories-types?product_id=' + productID)
      .pipe(map<any, any>(response => {
          const productList = new PaginationModel<any>();
          const headers = response.headers;
          productList.setHeaders(headers);
          productList.data = response.body.data as any[];
          return productList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getFilterAccessoriesTypeDropdown(requestParamModel: RequestParamModel) {
    return this.baseService.get('product/filter-accessories-types-dropdown', requestParamModel)
      .pipe(map<any, any>(response => {
          const productList = response.body.data as any[];
          return productList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  download(bodyData: any) {
    return this.baseService.post('product/download', bodyData)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // download product sizing table
  downloadProductSizingTable(productID: number, userID: any, typeID: number): any {
    return this.baseService.get('product/download-collection-size-table?product_id=' + productID + '&frontend_user_id=' + userID + '&product_type_id=' + typeID)
      .pipe(map<any, any>(response => {
          return response.body.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  downloadFileContent(bodyData: any) {
    return this.baseService.getDownloadFileContent(`product/download-resource?id=${bodyData.id}&type=${bodyData.type}`);
  }

  generateSticker(productID: number): any {
    return this.baseService.get('product/generate-sticker/' + productID)
      .pipe(map<any, any>(response => {
          return response.body.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  checkProductPreviouslyOrdered(productID: number, frontendUserID: number, requestParamModel?: RequestParamModel): Observable<ProductModel> {
    return this.baseService.get('product/check-product-previously-ordered/' + productID + '?frontend_user_id=' + frontendUserID, requestParamModel)
      .pipe(map<any, ProductModel>(response => {
          return response.body.data as ProductModel;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getConsumableCalculators(model: any) {
    // save user if new then add if old then update
    return this.baseService.post('consumable-calculation', model)
      .pipe(map<any, number>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }


  getComapnyName(requestParamModel: RequestParamModel): any {
    return this.baseService.get('product', requestParamModel)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<ProductModel>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as ProductModel[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  unleashedProductPriceSync(requestParamModel: RequestParamModel): any {
    return this.baseService.get('product/sync-price-from-unleashed', requestParamModel)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<ProductModel>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as ProductModel[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  // Get all sample packaging box list
  getSamplePackingBox(requestParamModel: RequestParamModel): any {
    return this.baseService.get('sample-packing-box', requestParamModel)
      .pipe(map<any, any>(response => {
          const accountList = new PaginationModel<ProductModel>();
          const headers = response.headers;
          accountList.setHeaders(headers);
          accountList.data = response.body.data as ProductModel[];
          return accountList;
        }),
        catchError(err => throwError(() => err))
      );
  }


  getProductCollectionList(requestParamModel: RequestParamModel): any {
    return this.baseService.get('product', requestParamModel)
      .pipe(map<any, any>(response => {
          const responseList = new PaginationModel<any>();
          const headers = response.headers;
          responseList.setHeaders(headers);
          responseList.data = response.body.data as any[];
          return responseList;
        }),
        catchError(err => throwError(() => err))
      );
  }

  reCalculateConsumable(payload: any): any {
    return this.baseService.post('quote-order/re-calculate-consumable', payload)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

  getProductLeadTime(id: any, payload: any): any {
    return this.baseService.post(`product/lead-time/${id}`, payload)
      .pipe(map<any, any>(response => {
          return response.data;
        }),
        catchError(err => throwError(() => err))
      );
  }

}
